import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { getApiFilters } from '~/shared/utils/apiFilters';
import { useAuthContext } from '~/shared/context/useAuthContext';

import { parseJsonLogicRule, formatConditionForDisplay } from '../utils/parseJsonLogicForTable';

import type { SelectOption, Account } from '~/shared/utils/types';
import type { JsonLogicRule, ParsedCondition } from '../utils/parseJsonLogicForTable';

const baseUrl = '/api/v1/volumes/autoscaling/rules';
const DEFAULT_REFETCH_INTERVAL_MS = 30000; // 30 seconds

export interface DryRunCategory {
  volumes: unknown[]; // Volume objects - not used in table, only aggregated stats
  count: number;
  totalCapacity: number;
  averageUtilization: number;
}

export interface DryRunReport {
  datafiable: DryRunCategory;
  potentiallyDatafiable: DryRunCategory;
  excluded: DryRunCategory;
}

export interface AutoscaleRuleFromApi {
  accountId: string;
  ruleId: string;
  active: boolean;
  rule: JsonLogicRule;
  dry_run_report: {
    rule: {
      accountId: string;
      ruleId: string;
      active: boolean;
      rule: JsonLogicRule | null;
    };
    datafiable: DryRunCategory;
    potentiallyDatafiable: DryRunCategory;
    excluded: DryRunCategory;
  };
}

export interface AutoscaleRulesApiResponse {
  rules: AutoscaleRuleFromApi[];
  total?: number;
}

/**
  type: string; // The raw type from JSON Logic (e.g., 'tags', 'instance_id')
  condition: string; // The display label is , is not
  operator: string; // The operator from JSON Logic (e.g., 'some', 'none', 'in')
 */
export interface RuleCondition {
  type: string;
  condition: string;
  operator: string;
  values: (string | Record<string, unknown>)[];
}

export interface AutoscaleRule {
  ruleId: string;
  enabled: boolean;
  conditions: RuleCondition[];
  volumes: number;
  capacity: number;
  utilization: string;
  exceptions: number;
  account: string;
  rawRule: JsonLogicRule;
  volumeData?: {
    datafiable: unknown[];
    potentiallyDatafiable: unknown[];
    excluded: unknown[];
  };
}

export interface AutoscaleRulesResponse {
  rules: AutoscaleRule[];
  count: number;
}

interface UseAutoscaleRulesQueryParams {
  // lightweight parsed filters (used for UI state)
  filters?: {
    active?: boolean[];
    ruleIds?: string[];
  };
  // raw api_filters search param value (JSON string) if available
  apiFilters?: string | null;
  dryRun?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Transform parsed conditions to display format
 */
function transformConditionsForDisplay(parsedConditions: ParsedCondition[]): RuleCondition[] {
  return parsedConditions.map((parsed) => {
    const formatted = formatConditionForDisplay(parsed);
    return {
      type: parsed.type,
      condition: formatted.type,
      operator: parsed.operator,
      values: formatted.values,
    };
  });
}

function getAccountNameById(accountId: string, accounts: Account[] | undefined): string {
  if (!accounts) {
    return '';
  }
  const account = accounts.find((acc) => acc.accountId === accountId);
  return account?.accountName || '';
}

function transformApiRuleToFrontend(
  apiRule: AutoscaleRuleFromApi,
  accounts: Account[] | undefined
): AutoscaleRule {
  // Parse JSON Logic rule to extract conditions
  const parsedConditions = parseJsonLogicRule(apiRule.rule);
  const conditions = transformConditionsForDisplay(parsedConditions);

  const report = apiRule.dry_run_report;

  // Datafiable volumes are the ones that match the rule
  const volumes = report.datafiable.count;
  const capacity = report.datafiable.totalCapacity;
  const utilization =
    volumes === 0 && report.datafiable.averageUtilization === 0
      ? 'N/A'
      : report.datafiable.averageUtilization
        ? `${Math.round(report.datafiable.averageUtilization)}%`
        : '0%';
  // Excluded volumes are the exceptions
  const exceptions = report.excluded.count;

  const accountName = getAccountNameById(apiRule.accountId, accounts);

  return {
    ruleId: apiRule.ruleId,
    enabled: apiRule.active,
    conditions,
    volumes,
    capacity,
    utilization,
    exceptions,
    account: accountName,
    rawRule: apiRule.rule,
    volumeData: {
      datafiable: report.datafiable.volumes,
      potentiallyDatafiable: report.potentiallyDatafiable.volumes,
      excluded: report.excluded.volumes,
    },
  };
}

/**
 * Hook to fetch list of autoscaling rules
 */
export function useAutoscaleRulesQuery({
  filters,
  apiFilters = null,
  dryRun = false,
  page = 0,
  limit = 50,
}: UseAutoscaleRulesQueryParams = {}) {
  const api = useXFetch();
  const { accounts } = useAuthContext();

  return useQuery<AutoscaleRulesResponse>({
    queryKey: ['autoscaleRules', filters, apiFilters, dryRun, page, limit],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (dryRun) {
        queryParams.set('dry_run', 'true');
      }

      // Add pagination parameters
      queryParams.set('limit', limit.toString());
      queryParams.set('offset', page.toString());

      if (apiFilters) {
        const parsed = JSON.parse(apiFilters) as SelectOption | SelectOption[];
        const processed = getApiFilters(parsed);
        const processedArray = Array.isArray(processed) ? processed : [processed];
        processedArray.forEach((f) => queryParams.append('filters', f));
      } else {
        if (filters?.active && filters.active.length > 0) {
          const filterParam = JSON.stringify({
            field: 'active',
            value: 'true',
            op: 'equals',
          });
          queryParams.append('filters', filterParam);
        }

        if (filters?.ruleIds && filters.ruleIds.length > 0) {
          const filterParam = JSON.stringify({
            field: 'ruleId',
            value: filters.ruleIds.length === 1 ? filters.ruleIds[0] : filters.ruleIds.join(','),
            op: filters.ruleIds.length === 1 ? 'equals' : 'in',
          });
          queryParams.append('filters', filterParam);
        }
      }

      const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

      const rulesResponse = await api<AutoscaleRulesApiResponse>(url);

      const rulesInFrontendFormat = (rulesResponse.rules || []).map((rule: AutoscaleRuleFromApi) =>
        transformApiRuleToFrontend(rule, accounts)
      );

      return {
        rules: rulesInFrontendFormat,
        count: rulesResponse.total ?? rulesInFrontendFormat.length,
      };
    },
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });
}
